import React, { useEffect, useState } from "react";
import { FiEdit2, FiRefreshCw, FiSave, FiSearch, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import axiosInstance from "../../libs/axios";
import { Pagination, PaginationType } from "../../utils/pagination.utils";
import { useDebounce } from "../../hooks/useDebounce";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const SHIFTS = [
  { value: "full_day", label: "Full Day" },
  { value: "morning", label: "Morning" },
  { value: "evening", label: "Evening" },
  { value: "off", label: "Off" },
];

interface Schedule {
  id: number;
  day_of_week: string;
  shift: string;
  is_working_day: boolean;
}

interface ScheduleUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username: string;
  employee_code?: string;
  department?: { id: number; name: string };
  work_schedules: Schedule[];
}

const AttendanceWorkSchedules: React.FC = () => {
  const [users, setUsers] = useState<ScheduleUser[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<ScheduleUser | null>(null);
  const [draft, setDraft] = useState<Schedule[]>([]);
  const [saving, setSaving] = useState(false);
  const [pagination, setPagination] = useState<PaginationType>({
    offset: 0,
    limit: 20,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  });

  const fetchSchedules = async (page = pagination.page, limit = pagination.limit) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/attendance/work-schedules", {
        params: { page, limit, search: search.trim() || undefined },
      });
      const data = response.data.data;
      setUsers(data.users || []);
      const p = data.pagination || {};
      setPagination({
        offset: p.offset ?? (page - 1) * limit,
        limit,
        page: p.page ?? page,
        total_pages: p.total_pages ?? 1,
        total_count: p.total_count ?? 0,
        has_next_page: !!p.has_next_page,
        has_prev_page: !!p.has_prev_page,
        next_page: p.next_page ?? null,
        prev_page: p.prev_page ?? null,
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch work schedules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search when debounced value changes
  useEffect(() => {
    fetchSchedules(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const openEditor = (user: ScheduleUser) => {
    setEditingUser(user);
    setDraft(DAYS.map((day) => {
      const existing = user.work_schedules.find((s) => s.day_of_week === day);
      return existing || { id: 0, day_of_week: day, is_working_day: day !== "saturday" && day !== "sunday", shift: day !== "saturday" && day !== "sunday" ? "full_day" : "off" };
    }));
  };

  const updateDraft = (day: string, changes: Partial<Schedule>) => {
    setDraft((prev) => prev.map((row) => {
      if (row.day_of_week !== day) return row;
      const next = { ...row, ...changes };
      if (!next.is_working_day) next.shift = "off";
      if (next.is_working_day && next.shift === "off") next.shift = "full_day";
      return next;
    }));
  };

  const saveDraft = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/attendance/work-schedules/${editingUser.id}`, { schedules: draft });
      toast.success("Work schedule updated");
      setEditingUser(null);
      fetchSchedules(pagination.page);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update work schedule");
    } finally {
      setSaving(false);
    }
  };

  const getUserName = (user: ScheduleUser) => `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Working Schedules</h1>
            <p className="text-sm text-gray-600 mt-1">Manage weekly working days and shifts for users.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchSchedules(1)}
                placeholder="Search users"
                className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <button onClick={() => fetchSchedules(1)} className="inline-flex items-center gap-2 px-3 py-2 bg-blue-800 text-white rounded text-sm">
              <FiRefreshCw /> Apply
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Department</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Schedule</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-600">No users found.</td></tr>
              ) : users.map((user) => (
                <tr key={user.id}>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{getUserName(user)}</div>
                    <div className="text-xs text-gray-500">{user.employee_code || user.username}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.department?.name || "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {user.work_schedules.filter((s) => s.is_working_day).map((s) => `${s.day_of_week.slice(0, 3)}: ${s.shift.replace("_", " ")}`).join(", ") || "No working days"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEditor(user)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50">
                      <FiEdit2 /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={(p) => fetchSchedules(p)} onLimitChange={(l) => fetchSchedules(1, l)} />
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setEditingUser(null)} />
          <div className="relative bg-white rounded shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">{getUserName(editingUser)}</h2>
              <button onClick={() => setEditingUser(null)} disabled={saving} className="p-2 hover:bg-gray-100 rounded"><FiX /></button>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {draft.map((row) => (
                <div key={row.day_of_week} className="border border-gray-200 rounded p-3">
                  <label className="flex items-center justify-between gap-3">
                    <span className="capitalize text-sm font-medium text-gray-800">{row.day_of_week}</span>
                    <input type="checkbox" checked={row.is_working_day} onChange={(e) => updateDraft(row.day_of_week, { is_working_day: e.target.checked })} />
                  </label>
                  <select
                    value={row.shift}
                    onChange={(e) => updateDraft(row.day_of_week, { shift: e.target.value })}
                    disabled={!row.is_working_day}
                    className="mt-3 w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                  >
                    {SHIFTS.map((shift) => <option key={shift.value} value={shift.value}>{shift.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button onClick={() => setEditingUser(null)} disabled={saving} className="px-4 py-2 border border-gray-300 rounded text-sm">Cancel</button>
              <button onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded text-sm disabled:bg-blue-400">
                <FiSave /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceWorkSchedules;
